import { createTheme } from "@mui/material/styles";
import { merged } from "./styles";

/**
 * Admin-portal theme — extends the main site theme without modifying it.
 *
 * Overrides:
 * - success: white text, darker hover shade (green family only)
 * - error:   darker hover shade (red family only)
 *
 * The main `merged` theme is untouched; this file is only imported by admin routes.
 */
export const adminTheme = createTheme(merged, {
  palette: {
    mode: "dark",
    background: {
      default: "#150D17",
      paper: "#1C1C22",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "rgba(255, 255, 255, 0.87)",
      disabled: "rgba(255, 255, 255, 0.6)",
    },
    action: {
      disabled: "rgba(255, 255, 255, 0.6)",
      disabledBackground: "rgba(255, 255, 255, 0.16)",
    },
    primary: {
      contrastText: "#FFFFFF",
    },
    secondary: {
      contrastText: "#FFFFFF",
    },
    info: {
      contrastText: "#FFFFFF",
    },
    warning: {
      contrastText: "#FFFFFF",
    },
    success: {
      contrastText: "#FFFFFF",
      dark: "#388E3C",
    },
    error: {
      dark: "#C62828",
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "#FFFFFF",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "#FFFFFF",
          "&.Mui-focused": {
            color: "#FFFFFF",
          },
          "&.Mui-disabled": {
            color: "rgba(255, 255, 255, 0.6)",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#FFFFFF",
          "&.Mui-focused": {
            color: "#FFFFFF",
          },
          "&.MuiInputLabel-shrink": {
            color: "#FFFFFF",
          },
          "&.Mui-disabled": {
            color: "rgba(255, 255, 255, 0.6)",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: "rgba(255, 255, 255, 0.87)",
          "&.Mui-disabled": {
            color: "rgba(255, 255, 255, 0.6)",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: "#FFFFFF",
          "&.Mui-disabled": {
            color: "rgba(255, 255, 255, 0.6)",
          },
        },
        input: {
          color: "#FFFFFF",
          WebkitTextFillColor: "#FFFFFF",
          "&::placeholder": {
            color: "rgba(255, 255, 255, 0.6)",
            opacity: 1,
          },
          "&.Mui-disabled": {
            WebkitTextFillColor: "rgba(255, 255, 255, 0.6)",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: "var(--mui-palette-text-primary)",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255, 255, 255, 0.22)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255, 255, 255, 0.34)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(216, 0, 252, 0.8)",
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255, 255, 255, 0.18)",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          color: "#FFFFFF",
        },
        icon: {
          color: "rgba(255, 255, 255, 0.87)",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: "#FFFFFF",
          "&.Mui-disabled": {
            color: "rgba(255, 255, 255, 0.6)",
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: "#FFFFFF",
        },
        secondary: {
          color: "rgba(255, 255, 255, 0.87)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          color: "#FFFFFF",
          backgroundColor: "#1C1C22",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          // success hover: stay green, go darker
          "&.MuiButton-containedSuccess:not(.Mui-disabled):hover": {
            backgroundColor: "#388E3C",
            color: "#FFFFFF",
          },
          // error hover: stay red, go darker
          "&.MuiButton-containedError:not(.Mui-disabled):hover": {
            backgroundColor: "#C62828",
            color: "#FFFFFF",
          },
        },
      },
    },
  },
});
